import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import CTAButton from '../ctaButton/CTAButton';

import garminWatch from '../../../content/media-library/card-screenshots/Garmin-Pay-watch-200-teal.png';
import appleWatch from '../../../content/media-library/card-screenshots/teal-hero-watch.png';
import horizontalCard from '../../../content/media-library/card-screenshots/debit-card-side.png';
import homepageCard from '../../../content/media-library/card-screenshots/teal-debit-card-side.png';
import ourTime from '../../../content/media-library/womens-euro/title-our-time.svg';
import fantasyFootball from '../../../content/media-library/womens-euro/title-fantasy-football.svg';
import appleWatchAndPhone from '../../../content/media-library/card-screenshots/apple-watch-phone.png';
import downloadWhite from '../../../content/media-library/download-white.svg';

const DynamicTitle = ({ text, fallback = '', param }) => {
  const value = useMemo(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    return params.get(param);
  }, [param]);

  return text ? <h2 className="featured-content__dynamic">{text.replace('%s', value || fallback)}</h2> : null;
};
DynamicTitle.propTypes = {
  text: PropTypes.string,
  fallback: PropTypes.string,
  param: PropTypes.string
};

const ForegroundImageTitle = ({ src, alt, className }) => {
  if (!src) return;
  return <img src={src} alt={alt} className={`featured-content__${className}`} />;
};

const ForegroundSide = (foregroundContent) => {
  switch (foregroundContent.type) {
    case 'Garmin watch':
      return (
        <LazyLoadImage
          src={garminWatch}
          alt="Starling Bank debit card in garmin watch"
          className="featured-content__phone-video-watch"
        />
      );
    case 'Apple watch':
      return (
        <LazyLoadImage
          src={appleWatch}
          alt="Starling Bank debit card in apple watch"
          className="featured-content__phone-video-watch"
        />
      );
    case 'Horizontal card':
      return (
        <LazyLoadImage
          src={horizontalCard}
          alt="Starling Bank debit card full view"
          className="featured-content__phone-video-card"
        />
      );
    case 'Homepage card':
      return (
        <LazyLoadImage
          src={homepageCard}
          alt="Starling Bank debit card full vertical view"
          className="featured-content__phone-video-vertical-card"
        />
      );
    default:
      return '';
  }
};

const FeaturedContent = ({
  preTitle,
  title,
  titleColour,
  subtitle,
  subtitleColour,
  paragraph,
  paragraphColour,
  callToAction,
  className,
  foregroundContent,
  children,
  backgroundImage,
  backgroundPosition,
  theme,
  mask,
  roundel,
  arrowDown,
  thin,
  superThin,
  centered,
  contentTop,
  dynamicTitle,
  titleLogo,
  postTextImage,
  award
}) => {
  let defaultClass = 'featured-content';
  if (centered || contentTop) defaultClass = 'featured-content-centered';

  const componentClasses = classNames(defaultClass, className, {
    'featured-content--content-top': contentTop,
    'featured-content--with-image': backgroundImage,
    'featured-content--with-foreground': foregroundContent,
    'featured-content--with-component': children,
    'featured-content--with-roundel': roundel,
    'featured-content--thin': thin,
    'featured-content--super-thin': superThin,
    [`featured-content--theme-${theme}`]: theme
  });

  const h1Classes = classNames('featured-content__title', {
    [`featured-content__title--${titleColour}`]: titleColour
  });

  const subtitleClasses = classNames('featured-content__subtitle', {
    [`featured-content__subtitle--${subtitleColour}`]: subtitleColour
  });

  const paragraphClasses = classNames('featured-content__paragraph', {
    [`featured-content__paragraph--${paragraphColour}`]: paragraphColour
  });

  const masks = {
    'darkest-navy': 'linear-gradient(rgba(26,35,67,0.65), rgba(26,35,67,0.65))',
    'dark-navy': 'linear-gradient(rgba(26,35,67,0.35), rgba(26,35,67,0.35))',
    purple: 'linear-gradient(rgba(38,14,82,0.5), rgba(38,14,82,0.5))'
  };

  let style = backgroundImage
    ? mask
      ? { backgroundImage: `${masks[mask]} , url(${backgroundImage})` }
      : { backgroundImage: `url(${backgroundImage})` }
    : null;

  style = backgroundPosition ? { ...style, backgroundPosition: backgroundPosition } : style;

  const scrollDown = (e) => {
    e.preventDefault();
    window.scroll({
      behavior: 'smooth',
      left: 0,
      top: document.getElementById('scroll-section').offsetTop - 120
    });
  };

  return (
    <section className={componentClasses} style={style}>
      <div className="featured-content__inner">
        <div className="featured-content__text-block">
          {preTitle ? <p className="featured-content__pretitle">{preTitle}</p> : null}
          {title ? <h1 className={h1Classes} dangerouslySetInnerHTML={{ __html: title }} /> : null}
          {titleLogo ? <img className="featured-content__title-logo" src={titleLogo.src} alt={titleLogo.alt} /> : null}
          {dynamicTitle && <DynamicTitle {...dynamicTitle} />}
          {subtitle && <p className={subtitleClasses} dangerouslySetInnerHTML={{ __html: subtitle }} />}
          {paragraph && <p className={paragraphClasses} dangerouslySetInnerHTML={{ __html: paragraph }} />}
          {callToAction && <CTAButton {...callToAction} />}
          {award && (
            <a href={award.href} target="_blank" rel="noopener noreferrer">
              <img key={award.alt || award.src} className="featured-content__award" src={award.src} alt={award.alt} />
            </a>
          )}
          {arrowDown && (
            <a href="#scroll-section" className="featured-content__button" onClick={(e) => scrollDown(e)}>
              <span className="screen-reader-text">Skip to main content</span>
              <img className="down-button" alt="Down arrow" src={downloadWhite} />
            </a>
          )}
          {postTextImage ? (
            postTextImage.link ? (
              <a target="_blank" rel="noopener noreferrer" href={postTextImage.link} className="featured-content__link">
                <img src={postTextImage.src} alt={postTextImage.alt} className="featured-content__posttextimage" />
              </a>
            ) : (
              <img src={postTextImage.src} alt={postTextImage.alt} className="featured-content__posttextimage" />
            )
          ) : null}
        </div>

        {foregroundContent && foregroundContent.type === 'Apple watch and phone' ? (
          <div className="featured-content__image">
            <img
              src={appleWatchAndPhone}
              alt="Starling Bank debit card full vertical view"
              className="featured-content__phone-watch"
            />
          </div>
        ) : null}

        {foregroundContent &&
        foregroundContent.type !== 'Apple watch and phone' &&
        foregroundContent.type !== 'Our Time' &&
        foregroundContent.type !== 'Fantasy Football' ? (
          <div className="featured-content__phone-video">
            <ForegroundSide {...foregroundContent} />
            <div
              className={`phone-case featured-content__phone-video-phone ${
                foregroundContent.type === 'Homepage card' ? ' featured-content-with-vertical-card' : ''
              } ${!foregroundContent.type ? ' featured-content-with-no-card' : ''}`}
            >
              <div className="phone-case-container">
                <div className="phone-case-container-screen">
                  {foregroundContent.video ? (
                    <video muted loop autoPlay poster={foregroundContent.video.preview}>
                      <source
                        src={`https://media.starlingbank.com/videos/${foregroundContent.video.id}.webm`}
                        type="video/webm; codecs=vp9,vorbis"
                      />
                      <source
                        src={`https://media.starlingbank.com/videos/${foregroundContent.video.id}.mp4`}
                        type="video/mp4"
                      />
                    </video>
                  ) : null}
                  {foregroundContent.image ? (
                    <img src={foregroundContent.placeholder || foregroundContent.image} alt={foregroundContent.alt} />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {children ? <div className="featured-content__cs-component">{children}</div> : null}
      </div>

      {foregroundContent?.type === 'Our Time' ? (
        <ForegroundImageTitle src={ourTime} alt="Our Time - Starling Bank" className="our-time" />
      ) : null}

      {foregroundContent?.type === 'Fantasy Football' ? (
        <ForegroundImageTitle src={fantasyFootball} alt="Women’s EURO Fantasy Football" className="fantasy-football" />
      ) : null}
    </section>
  );
};

FeaturedContent.propTypes = {
  preTitle: PropTypes.string,
  title: PropTypes.string,
  titleColour: PropTypes.string,
  subtitle: PropTypes.string,
  subtitleColour: PropTypes.string,
  paragraph: PropTypes.string,
  paragraphColour: PropTypes.string,
  callToAction: PropTypes.shape(CTAButton.propTypes),
  foregroundContent: PropTypes.object,
  children: PropTypes.array,
  className: PropTypes.string,
  backgroundImage: PropTypes.string,
  backgroundPosition: PropTypes.string,
  theme: PropTypes.oneOf([null, 'sand']),
  mask: PropTypes.string,
  roundel: PropTypes.string,
  arrowDown: PropTypes.oneOf(['white']),
  thin: PropTypes.string,
  superThin: PropTypes.string,
  centered: PropTypes.string,
  contentTop: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  dynamicTitle: PropTypes.object,
  titleLogo: PropTypes.object,
  postTextImage: PropTypes.shape({ src: PropTypes.string, alt: PropTypes.string }),
  award: PropTypes.object
};

export default FeaturedContent;
